import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { isSubscriptionActive } from '../../utils/subscriptions';
import { formatUsd } from '../../data/studyPlans';
import { categoryLabel, paymentDateMs } from '../../utils/payments';
import { startOfMonth, startOfWeek, endOfDay } from '../../utils/exportExcel';

function BarChart({ items, maxHeight = 120 }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="sba-admin-chart">
      {items.map((item) => (
        <div key={item.label} className="sba-admin-chart-col">
          <div className="sba-admin-chart-bar-wrap" style={{ height: maxHeight }}>
            <div
              className="sba-admin-chart-bar"
              style={{
                height: `${Math.max(4, (item.value / max) * 100)}%`,
                background: item.color || '#1b365d',
              }}
              title={`${item.label}: ${item.value}`}
            />
          </div>
          <span className="sba-admin-chart-label">{item.label}</span>
          <span className="sba-admin-chart-val">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboardPanel({ onNavigate }) {
  const [inquiries, setInquiries] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [modules, setModules] = useState([]);
  const [daysByModule, setDaysByModule] = useState({});
  const [students, setStudents] = useState([]);

  useEffect(() => {
    const unsubs = [
      onSnapshot(
        query(collection(db, 'registrationInquiries'), orderBy('createdAt', 'desc'), limit(200)),
        (snap) => setInquiries(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(
        query(collection(db, 'subscriptions'), orderBy('createdAt', 'desc'), limit(200)),
        (snap) => setSubscriptions(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(
        query(collection(db, 'payments'), orderBy('createdAt', 'desc'), limit(300)),
        (snap) => setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(
        query(collection(db, 'modules'), orderBy('order', 'asc')),
        (snap) => setModules(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
      onSnapshot(
        query(collection(db, 'users'), where('role', '==', 'student'), limit(200)),
        (snap) => setStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setStudents([])
      ),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  useEffect(() => {
    const dayUnsubs = {};
    modules.forEach((mod) => {
      if (!dayUnsubs[mod.id]) {
        dayUnsubs[mod.id] = onSnapshot(
          query(collection(db, 'modules', mod.id, 'days'), orderBy('order', 'asc')),
          (dSnap) => {
            setDaysByModule((prev) => ({
              ...prev,
              [mod.id]: dSnap.docs.length,
            }));
          }
        );
      }
    });
    return () => Object.values(dayUnsubs).forEach((u) => u());
  }, [modules]);

  const now = new Date();
  const weekStart = startOfWeek(now).getTime();
  const monthStart = startOfMonth(now).getTime();
  const weekEnd = endOfDay(now).getTime();

  const kpis = useMemo(() => {
    const newInquiries = inquiries.filter((i) => i.status === 'new').length;
    const activeSubs = subscriptions.filter(isSubscriptionActive).length;
    const monthPayments = payments.filter((p) => {
      const ms = paymentDateMs(p);
      return ms >= monthStart && ms <= weekEnd;
    });
    const weekPayments = payments.filter((p) => {
      const ms = paymentDateMs(p);
      return ms >= weekStart && ms <= weekEnd;
    });
    const monthRevenue = monthPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const weekRevenue = weekPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    const lectureCount = Object.values(daysByModule).reduce((sum, n) => sum + (n || 0), 0);

    const unpaidEnrolled = students.filter((u) => u.accessStatus === 'pending_payment').length;

    return {
      newInquiries,
      totalInquiries: inquiries.length,
      activeSubs,
      unpaidEnrolled,
      totalStudents: students.length || activeSubs,
      monthRevenue,
      weekRevenue,
      monthPaymentCount: monthPayments.length,
      modules: modules.length,
      lectures: lectureCount,
    };
  }, [
    inquiries,
    subscriptions,
    payments,
    students,
    modules,
    daysByModule,
    monthStart,
    weekStart,
    weekEnd,
  ]);

  const inquiryByStatus = useMemo(() => {
    const statuses = [
      { id: 'new', label: 'New' },
      { id: 'contacted', label: 'Contacted' },
      { id: 'enrolled', label: 'Enrolled' },
      { id: 'closed', label: 'Closed' },
    ];
    return statuses.map((status, index) => ({
      label: status.label,
      value: inquiries.filter((item) => (item.status || 'new') === status.id).length,
      color: index % 2 === 0 ? '#1B365D' : '#005F61',
    }));
  }, [inquiries]);

  const recentInquiries = inquiries.slice(0, 5);
  const recentPayments = payments.slice(0, 5);

  function formatDate(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  }

  return (
    <div className="sba-admin-panel">
      <div className="sba-home">
        <section className="sba-welcome">
          <h2>Academy overview</h2>
          <p>
            {kpis.newInquiries === 1
              ? '1 new inquiry is waiting.'
              : `${kpis.newInquiries} new inquiries are waiting.`}
          </p>
          <button type="button" className="sba-btn-gold" onClick={() => onNavigate?.('inquiries')}>
            Review inquiries
          </button>
        </section>

        <div className="sba-stat-grid">
          {[
            { label: 'New inquiries', value: kpis.newInquiries, tab: 'inquiries' },
            { label: 'Active subscriptions', value: kpis.activeSubs, tab: 'subscriptions' },
            { label: 'Unpaid enrollments', value: kpis.unpaidEnrolled, tab: 'unpaid-enrollments' },
            { label: 'Revenue this month', value: formatUsd(kpis.monthRevenue), tab: 'payments' },
          ].map((item) => (
            <button key={item.label} type="button" className="sba-stat" onClick={() => onNavigate?.(item.tab)}>
              <span className="sba-stat-n">{item.value}</span>
              <span className="sba-stat-l">{item.label}</span>
            </button>
          ))}
        </div>

        <section className="sba-panel">
          <h2>Inquiries by status</h2>
          <BarChart items={inquiryByStatus} />
        </section>

        <div className="sba-stat-grid">
          <section className="sba-panel">
            <h2>Recent inquiries</h2>
            {recentInquiries.length === 0 ? (
              <p className="sba-stat-l">No inquiries yet.</p>
            ) : (
              <ul className="sba-home-list">
                {recentInquiries.map((inquiry) => (
                  <li key={inquiry.id}>
                    <div>
                      <strong>{inquiry.fullName}</strong>
                      <span>{inquiry.email}</span>
                    </div>
                    <span>
                      {inquiry.status || 'new'} · {formatDate(inquiry.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="sba-panel">
            <h2>Recent payments</h2>
            {recentPayments.length === 0 ? (
              <p className="sba-stat-l">No payments yet.</p>
            ) : (
              <ul className="sba-home-list">
                {recentPayments.map((payment) => (
                  <li key={payment.id}>
                    <div>
                      <strong>{payment.referenceNumber || payment.label || 'Payment'}</strong>
                      <span>{categoryLabel(payment.category)}</span>
                    </div>
                    <span>
                      {formatUsd(payment.amount)} · {formatDate(payment.paymentDate || payment.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
